import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Users, Shield, User, Trash2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  
  const fetchUsers = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };
  
  const handleRoleChange = async (userId, newRole) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${userId}/role`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ role: newRole })
      });
      
      if (response.ok) {
        toast.success(`Rol actualizado a ${newRole}`);
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al actualizar rol');
      }
    } catch (error) {
      console.error('Error updating role:', error);
      toast.error('Error al actualizar rol');
    }
  };
  
  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/users/${selectedUser.user_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        toast.success('Usuario eliminado');
        setIsDeleteDialogOpen(false);
        setSelectedUser(null);
        fetchUsers();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Error al eliminar usuario');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Error al eliminar usuario');
    }
  };
  
  const handleCheckExpiring = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/check-expiring-payments`, {
        method: 'POST',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success(`${data.notifications_created} notificaciones creadas`);
      }
    } catch (error) {
      console.error('Error checking payments:', error);
      toast.error('Error al verificar pagos');
    }
  };
  
  const adminCount = users.filter(u => u.role === 'admin').length;
  const studentCount = users.filter(u => u.role === 'student').length;
  
  return (
    <div className="min-h-screen bg-black page-transition">
      {/* Header */}
      <div className="bg-zinc-950 border-b border-zinc-800 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <Button onClick={() => navigate('/dashboard')} variant="ghost" size="sm" data-testid="back-button">
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h1 className="text-2xl font-heading text-white uppercase">GESTIÓN DE USUARIOS</h1>
          </div>
          
          <Button
            onClick={handleCheckExpiring}
            data-testid="check-expiring-button"
            className="bg-primary hover:bg-[#b20710] text-white rounded-sm font-bold uppercase tracking-wider"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            VERIFICAR PAGOS
          </Button>
        </div>
      </div>
      
      {/* Stats */}
      <div className="max-w-7xl mx-auto p-4">
        <div className="grid grid-cols-2 gap-4">
          <Card className="bg-zinc-950 border-zinc-800 p-4" data-testid="admin-count">
            <div className="flex items-center space-x-3">
              <Shield className="h-8 w-8 text-primary" />
              <div>
                <span className="text-xs uppercase tracking-wider text-zinc-400">Administradores</span>
                <div className="text-3xl font-bold font-heading text-white">{adminCount}</div>
              </div>
            </div>
          </Card>
          
          <Card className="bg-zinc-950 border-zinc-800 p-4" data-testid="student-count">
            <div className="flex items-center space-x-3">
              <Users className="h-8 w-8 text-blue-500" />
              <div>
                <span className="text-xs uppercase tracking-wider text-zinc-400">Estudiantes</span>
                <div className="text-3xl font-bold font-heading text-white">{studentCount}</div>
              </div>
            </div>
          </Card>
        </div>
      </div>
      
      {/* Users List */}
      <div className="max-w-7xl mx-auto p-4 pb-20">
        <Card className="bg-zinc-950 border-zinc-800">
          {loading ? (
            <div className="p-8 text-center text-zinc-500">Cargando...</div>
          ) : (
            <div className="divide-y divide-zinc-800">
              {users.map((user) => (
                <div 
                  key={user.user_id} 
                  className="p-4 flex items-center justify-between hover:bg-zinc-900/50 transition-colors"
                  data-testid={`user-row-${user.user_id}`}
                >
                  <div className="flex items-center space-x-3">
                    {user.picture ? (
                      <img 
                        src={user.picture} 
                        alt={user.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center">
                        <User className="h-5 w-5 text-zinc-600" />
                      </div>
                    )}
                    <div>
                      <p className="text-white font-medium">{user.name}</p>
                      <p className="text-sm text-zinc-400">{user.email}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    <Select 
                      value={user.role} 
                      onValueChange={(value) => handleRoleChange(user.user_id, value)}
                    >
                      <SelectTrigger 
                        className={`w-32 ${user.role === 'admin' ? 'bg-primary/20 border-primary/50 text-primary' : 'bg-zinc-900 border-zinc-800 text-white'}`}
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-zinc-900 border-zinc-800 text-white">
                        <SelectItem value="admin">Admin</SelectItem>
                        <SelectItem value="student">Estudiante</SelectItem>
                      </SelectContent>
                    </Select>
                    
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setSelectedUser(user);
                        setIsDeleteDialogOpen(true);
                      }}
                      data-testid={`delete-user-${user.user_id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
      
      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 text-white">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl uppercase text-red-500">Eliminar Usuario</DialogTitle>
            <DialogDescription className="text-zinc-400">
              ¿Estás seguro de eliminar a {selectedUser?.name}? Esta acción no se puede deshacer.
            </DialogDescription>
          </DialogHeader>
          <div className="flex space-x-3 mt-4">
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleDeleteUser}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
            >
              Eliminar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminUsers;
